'use client'

import { useRef, useState, useEffect } from 'react'
import { Image } from '@vinted/web-ui'

import { getTestId } from '@marketplace-web/vendor-abstractions/web-ui-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { viewNextItemImageEvent } from '@marketplace-web/item-box/item-box-data'

import ImageCarouselIndicators from './ImageCarouselIndicators'

type Props = {
  images: Array<string>
  imageColor?: string | null
  accessibilityLabel?: string
  url?: string
  onClick?: (event: React.MouseEvent) => void
  openInNewTab?: boolean
  testId?: string
  renderDesktopFallback: () => React.ReactNode
  itemId: number
}

const ImageCarousel = ({
  images,
  imageColor,
  accessibilityLabel,
  url,
  onClick,
  openInNewTab = false,
  testId,
  renderDesktopFallback,
  itemId,
}: Props) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loadedIndices, setLoadedIndices] = useState<Set<number>>(() => new Set([0]))
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)
  const [hasDragged, setHasDragged] = useState(false)
  const { track } = useTracking()
  const prevIndexRef = useRef(0)

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return undefined

    const handleScroll = () => {
      const itemWidth = container.offsetWidth
      const newIndex = Math.round(container.scrollLeft / itemWidth)
      const isNewImage = newIndex !== prevIndexRef.current

      if (isNewImage) {
        track(
          viewNextItemImageEvent({
            imagePosition: newIndex + 1,
            numberItemImages: images.length,
            itemId,
          }),
        )
      }

      prevIndexRef.current = newIndex
      setCurrentIndex(newIndex)
      setLoadedIndices(indices => {
        if (indices.has(newIndex)) return indices

        return new Set([...indices, newIndex])
      })
    }

    container.addEventListener('scroll', handleScroll)

    return () => {
      container.removeEventListener('scroll', handleScroll)
    }
  }, [images.length, itemId, track])

  const shouldLoadImage = (index: number): boolean => {
    return loadedIndices.has(index)
  }

  const handleMouseDown = (event: React.MouseEvent) => {
    if (!scrollContainerRef.current) return
    setIsDragging(true)
    setHasDragged(false)
    setStartX(event.pageX - scrollContainerRef.current.offsetLeft)
    setScrollLeft(scrollContainerRef.current.scrollLeft)
  }

  const handleMouseLeave = () => {
    setIsDragging(false)
  }

  const handleMouseUp = () => {
    setIsDragging(false)
    // Defer reset to next tick so handleClick sees the drag state
    setTimeout(() => setHasDragged(false), 0)
  }

  const handleMouseMove = (event: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return
    event.preventDefault()
    const pageX = event.pageX - scrollContainerRef.current.offsetLeft
    const walk = (pageX - startX) * 3 // Higher multiplier = more sensitive
    if (Math.abs(walk) > 5) {
      setHasDragged(true)
    }
    scrollContainerRef.current.scrollLeft = scrollLeft - walk
  }

  const handleClick = (event: React.MouseEvent) => {
    if (hasDragged) {
      event.preventDefault()
      event.stopPropagation()

      return
    }

    if (onClick) {
      onClick(event)
    }

    if (url && !event.defaultPrevented) {
      if (openInNewTab) {
        window.open(url, '_blank', 'noopener,noreferrer')
      } else {
        window.location.href = url
      }
    }
  }

  return (
    <>
      <div className="new-item-box__carousel-container">
        <div
          role="presentation"
          ref={scrollContainerRef}
          className="new-item-box__carousel"
          data-testid={getTestId(testId, 'carousel')}
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseLeave}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          onClick={handleClick}
        >
          {images.map((image, index) => (
            <div
              key={index}
              className="new-item-box__carousel-item"
              onDragStart={event => event.preventDefault()}
            >
              <Image
                src={shouldLoadImage(index) ? image : null}
                scaling="cover"
                ratio="portrait"
                color={imageColor}
                alt={accessibilityLabel}
                testId={getTestId(testId, `carousel-image-${index}`)}
              />
            </div>
          ))}
        </div>
        {images.length > 1 && (
          <div
            className="new-item-box__carousel-indicators"
            data-testid={getTestId(testId, 'carousel-indicators')}
          >
            <ImageCarouselIndicators
              totalImages={images.length}
              currentIndex={currentIndex}
              testId={testId}
            />
          </div>
        )}
      </div>
      <div className="new-item-box__carousel-desktop-fallback">{renderDesktopFallback()}</div>
    </>
  )
}

export default ImageCarousel
